import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { WalletProvider } from './WalletContext';
import api from './services/api';
import { authStorage } from './services/storage';
import MultiWalletSelector from './components/MultiWalletSelector';
import Login from './components/Login';
import Analytics from './components/Analytics';
import Transactions from './components/Transactions';
import Recurring from './components/Recurring';
import Households from './components/Households';
import HouseholdDashboard from './components/HouseholdDashboard';
import Settings from './components/Settings';
import Wallets from './components/Wallets';
import Exchange from './components/Exchange';
import Reports from './components/Reports';
import Home from './components/Home';
import TimeOff from './components/TimeOff';
import TimeOffDebug from './components/TimeOffDebug';
import Employment from './components/Employment';
import AddIncomeModal from './components/AddIncomeModal';
import AddExpenseModal from './components/AddExpenseModal';
import CreateHouseholdModal from './components/CreateHouseholdModal';
import JoinHouseholdModal from './components/JoinHouseholdModal';
import Onboarding from './components/Onboarding';
import Signup from './components/Signup';
import './App.css';
import './styles/mobile-glassmorphism.css';
import './styles/responsive-scaling.css';
import './styles/mobile-fixes.css';
import BottomNav from './components/BottomNav';
import MobileHeader from './components/MobileHeader';
import './components/MobileHeader.css';

function Sidebar({ isOpen, toggleSidebar }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [showIncomeModal, setShowIncomeModal] = useState(false);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [showCreateHouseholdModal, setShowCreateHouseholdModal] = useState(false);
  const [showJoinHouseholdModal, setShowJoinHouseholdModal] = useState(false);
  const [userName, setUserName] = useState('');
  
  // Collapsible sections state
  const [expandedSections, setExpandedSections] = useState({
    money: true,
    household: true,
    work: true
  });

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const token = authStorage.getToken();
        if (!token) return;
        
        const response = await api.get('/auth/profile');
        const displayName = response.data.name || response.data.email || 'User';
        setUserName(displayName);
      } catch (error) {
        console.error('Error fetching profile:', error);
      }
    };
    
    fetchUserProfile();
    
    const handleProfileUpdate = (event) => {
      if (event.detail?.name) {
        setUserName(event.detail.name);
      }
    };
    
    window.addEventListener('userProfileUpdated', handleProfileUpdate);
    return () => window.removeEventListener('userProfileUpdated', handleProfileUpdate);
  }, []);

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleLogout = async () => {
    await authStorage.clearAuth();
    navigate('/login');
  };

  return (
    <>
      <div className={`sidebar ${isOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <h2>🏠 DomusAI</h2>
          <button className="sidebar-toggle" onClick={toggleSidebar}>
            {isOpen ? '✕' : '☰'}
          </button>
        </div>

        <MultiWalletSelector />

        <nav className="sidebar-nav">
          {/* Home - Always visible */}
          <button className={`nav-item ${isActive('/home') ? 'active' : ''}`} onClick={() => navigate('/home')}>
            <span className="nav-icon">🏡</span>
            <span className="nav-label">Home</span>
          </button>

          {/* MONEY Section */}
          <div className="nav-section">
            <button className="nav-section-header" onClick={() => toggleSection('money')}>
              <span className="nav-section-icon">💰</span>
              <span className="nav-section-title">Money</span>
              <span className={`nav-section-arrow ${expandedSections.money ? 'expanded' : ''}`}>›</span>
            </button>
            {expandedSections.money && (
              <div className="nav-section-items">
                <button className={`nav-item nav-item-child ${isActive('/analytics') ? 'active' : ''}`} onClick={() => navigate('/analytics')}>
                  <span className="nav-icon">📊</span>
                  <span className="nav-label">Analytics</span>
                </button>
                <button className={`nav-item nav-item-child ${isActive('/transactions') ? 'active' : ''}`} onClick={() => navigate('/transactions')}>
                  <span className="nav-icon">📋</span>
                  <span className="nav-label">Transactions</span>
                </button>
                <button className={`nav-item nav-item-child ${isActive('/recurring') ? 'active' : ''}`} onClick={() => navigate('/recurring')}>
                  <span className="nav-icon">🔄</span>
                  <span className="nav-label">Recurring</span>
                </button>
                <button className={`nav-item nav-item-child ${isActive('/wallets') ? 'active' : ''}`} onClick={() => navigate('/wallets')}>
                  <span className="nav-icon">💳</span>
                  <span className="nav-label">Wallets</span>
                </button>
                <button className={`nav-item nav-item-child ${isActive('/reports') ? 'active' : ''}`} onClick={() => navigate('/reports')}>
                  <span className="nav-icon">📑</span>
                  <span className="nav-label">Reports</span>
                </button>
              </div>
            )}
          </div>

          {/* HOUSEHOLD Section */}
          <div className="nav-section">
            <button className="nav-section-header" onClick={() => toggleSection('household')}>
              <span className="nav-section-icon">🏠</span>
              <span className="nav-section-title">Household</span>
              <span className={`nav-section-arrow ${expandedSections.household ? 'expanded' : ''}`}>›</span>
            </button>
            {expandedSections.household && (
              <div className="nav-section-items">
                <button className={`nav-item nav-item-child ${isActive('/households') ? 'active' : ''}`} onClick={() => navigate('/households')}>
                  <span className="nav-icon">👨‍👩‍👧</span>
                  <span className="nav-label">My Household</span>
                </button>
              </div>
            )}
          </div>

          {/* WORK Section */}
          <div className="nav-section">
            <button className="nav-section-header" onClick={() => toggleSection('work')}>
              <span className="nav-section-icon">💼</span>
              <span className="nav-section-title">Work</span>
              <span className={`nav-section-arrow ${expandedSections.work ? 'expanded' : ''}`}>›</span>
            </button>
            {expandedSections.work && (
              <div className="nav-section-items">
                <button className={`nav-item nav-item-child ${isActive('/employment') ? 'active' : ''}`} onClick={() => navigate('/employment')}>
                  <span className="nav-icon">🏢</span>
                  <span className="nav-label">Employment</span>
                </button>
                <button className={`nav-item nav-item-child ${isActive('/timeoff') ? 'active' : ''}`} onClick={() => navigate('/timeoff')}>
                  <span className="nav-icon">🏖️</span>
                  <span className="nav-label">Time Off</span>
                </button>
              </div>
            )}
          </div>

          {/* Settings - Always visible at bottom of nav */}
          <div className="nav-section-divider"></div>
          <button className={`nav-item ${isActive('/settings') ? 'active' : ''}`} onClick={() => navigate('/settings')}>
            <span className="nav-icon">⚙️</span>
            <span className="nav-label">Settings</span>
          </button>
        </nav>

        <div className="sidebar-quick-actions">
          <div className="quick-actions-title">Quick Actions</div>
          <button className="quick-action-btn income" onClick={() => setShowIncomeModal(true)}>
            <span className="qa-icon">💰</span>
            <span className="qa-label">Add Income</span>
          </button>
          <button className="quick-action-btn expense" onClick={() => setShowExpenseModal(true)}>
            <span className="qa-icon">💸</span>
            <span className="qa-label">Add Expense</span>
          </button>
          <button className="quick-action-btn exchange" onClick={() => navigate('/exchange')}>
            <span className="qa-icon">💱</span>
            <span className="qa-label">Exchange</span>
          </button>
        </div>

        <div className="sidebar-footer">
          <div className="user-info">
            <span className="user-icon">👤</span>
            <span className="user-name">Welcome, {userName}</span>
          </div>
          <button className="logout-btn" onClick={handleLogout}>
            <span>🚪</span> Logout
          </button>
        </div>
      </div>

      {showIncomeModal && (
        <AddIncomeModal onClose={() => { setShowIncomeModal(false); window.location.reload(); }} />
      )}
      {showExpenseModal && (
        <AddExpenseModal onClose={() => { setShowExpenseModal(false); window.location.reload(); }} />
      )}
      {showCreateHouseholdModal && (
        <CreateHouseholdModal
          onClose={() => setShowCreateHouseholdModal(false)}
          onSuccess={() => { setShowCreateHouseholdModal(false); window.location.reload(); }}
        />
      )}
      {showJoinHouseholdModal && (
        <JoinHouseholdModal
          onClose={() => setShowJoinHouseholdModal(false)}
          onSuccess={() => { setShowJoinHouseholdModal(false); window.location.reload(); }}
        />
      )}
    </>
  );
}

function AppContent() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const initApp = async () => {
      console.log('[App] Initializing...');
      
      // Load auth from storage
      const { token } = await authStorage.loadAuth();
      
      console.log('[App] Auth loaded, hasToken:', !!token);
      
      // If we have a token, verify it's still valid
      if (token) {
        try {
          // Quick validation - try to get user info
          await api.get('/auth/me');
          setIsAuthenticated(true);
          console.log('[App] Token valid, authenticated');
          // Notify WalletContext that auth is ready
          window.dispatchEvent(new Event('authReady'));
        } catch (error) {
          console.log('[App] Token invalid, clearing auth');
          await authStorage.clearAuth();
          setIsAuthenticated(false);
        }
      } else {
        setIsAuthenticated(false);
      }
      
      setIsInitialized(true);
    };
    
    initApp();
  }, []);

  // Listen for auth changes (only after initialized)
  useEffect(() => {
    // Don't start checking until we've done initial auth check
    if (!isInitialized) return;
    
    const checkAuth = () => {
      const hasToken = authStorage.isAuthenticated();
      setIsAuthenticated(hasToken);
    };
    
    // Listen for authReady event (from Signup/Login)
    const handleAuthReady = () => {
      console.log('[App] authReady event received, setting authenticated');
      setIsAuthenticated(true);
    };
    window.addEventListener('authReady', handleAuthReady);
    
    // Check auth periodically (for logout from other components)
    const interval = setInterval(checkAuth, 1000);
    return () => {
      clearInterval(interval);
      window.removeEventListener('authReady', handleAuthReady);
    };
  }, [isInitialized]);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (isInitialized && !isAuthenticated && 
        location.pathname !== '/login' && 
        location.pathname !== '/signup' &&
        location.pathname !== '/onboarding') {
      navigate('/login');
    }
  }, [isInitialized, isAuthenticated, location.pathname, navigate]);

  // Show loading while checking authentication
  if (!isInitialized) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        background: 'linear-gradient(135deg, #f8faff 0%, #e8f0fe 100%)'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🏠</div>
          <div style={{ fontSize: '18px', color: '#5f6368' }}>Loading DomusAI...</div>
        </div>
      </div>
    );
  }

  // Handle login page (allow access without auth)
  if (location.pathname === '/login') {
    // If already authenticated, redirect to app
    if (isAuthenticated) {
      return <Navigate to="/home" replace />;
    }
    return <Login onLoginSuccess={() => setIsAuthenticated(true)} />;
  }

  // Handle signup page (allow access without auth)
  if (location.pathname === '/signup') {
    // If already authenticated, redirect to app
    if (isAuthenticated) {
      return <Navigate to="/home" replace />;
    }
    return <Signup />;
  }

  // Handle onboarding page - check token directly to avoid race condition
  if (location.pathname === '/onboarding') {
    if (isAuthenticated || authStorage.isAuthenticated()) {
      return <Onboarding />;
    }
    return <Navigate to="/login" replace />;
  }

  // CRITICAL: Block ALL non-login routes if not authenticated
  // This must happen BEFORE rendering any app content
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="app-container">
      <Sidebar isOpen={sidebarOpen} toggleSidebar={() => setSidebarOpen(!sidebarOpen)} />
      <div className={`main-content ${sidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
        <MobileHeader />
        <BottomNav />
        <Routes>
          <Route path="/home" element={<Home />} />
          <Route path="/analytics" element={<Analytics />} />
          <Route path="/transactions" element={<Transactions />} />
          <Route path="/recurring" element={<Recurring />} />
          <Route path="/households" element={<Households />} />
          <Route path="/household/:id" element={<HouseholdDashboard />} />
          <Route path="/timeoff" element={<TimeOff />} />
          <Route path="/timeoff-debug" element={<TimeOffDebug />} />
          <Route path="/employment" element={<Employment />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/wallets" element={<Wallets />} />
          <Route path="/exchange" element={<Exchange />} />
          <Route path="/reports" element={<Reports />} />
          <Route path="/" element={<Navigate to="/home" />} />
          <Route path="/dashboard" element={<Navigate to="/home" />} />
          <Route path="/analytics-old" element={<Navigate to="/analytics" />} />
        </Routes>
      </div>
    </div>
  );
}

function App() {
  return (
    <Router>
      <WalletProvider>
        <AppContent />
      </WalletProvider>
    </Router>
  );
}

export default App;
